import { Counter, LinkButton, Price, Typography } from 'fronton-react';
import React, { ChangeEvent, ReactNode } from 'react';
import { useBreakpoints } from 'common/hooks/use-breakpoints';
import { getImageSize } from 'pages/common/helpers/get-image-size';
import { ProductArticle } from 'pages/common/components/ProductArticle';
import { SMALL_FALLBACK_PRODUCT_IMAGE_URL } from 'pages/common/consts/urls';
import {
  MainProductContainer,
  MainProductImageAndName,
  PriceColumnContainer,
  MainProductName,
  MainProductInfo,
  MainProductInfoRow,
  MainProductInfoColumn,
  CounterAndSelectTintColorContainer,
  ItemLink,
  ProductImgWithLink,
} from './styles';

type TProps = {
  photo?: string;
  displayedName?: string;
  productId?: string;
  count: string;
  price: number;
  onChangeCounter: (_event: ChangeEvent<HTMLInputElement>, value: string) => void;
  selectTintColorComponent?: ReactNode;
  priceUnitsLabel?: string;
  productPageName?: string;
};

export const MainProduct = ({
  photo,
  displayedName,
  productId,
  count,
  price,
  onChangeCounter,
  selectTintColorComponent,
  priceUnitsLabel = '₽ / шт.',
  productPageName,
}: TProps) => {
  const { mobile } = useBreakpoints() ?? {};
  const imageSize = getImageSize(mobile);
  const pricePerOneItem = (price / Number(count)).toFixed(2);
  const hasProductUrl = Boolean(productPageName);
  const imgSrc = photo ?? SMALL_FALLBACK_PRODUCT_IMAGE_URL;

  const handleOpenProduct = () => {
    window.open(`/product/${productPageName}`);
  };

  return (
    <MainProductContainer data-testid="main-product">
      <MainProductImageAndName>
        {hasProductUrl ? (
          <ProductImgWithLink
            src={imgSrc}
            alt={displayedName}
            loading="lazy"
            width={imageSize}
            height={imageSize}
            onClick={handleOpenProduct}
            data-testid="main-product-image"
          />
        ) : (
          <img
            src={imgSrc}
            alt={displayedName}
            loading="lazy"
            width={imageSize}
            height={imageSize}
            data-testid="main-product-image"
          />
        )}
        <MainProductInfoColumn>
          <MainProductInfoRow>
            <MainProductName>
              <MainProductInfo data-testid="main-product-article">
                <ProductArticle productId={productId} />
              </MainProductInfo>
              {hasProductUrl ? (
                <LinkButton
                  className={ItemLink}
                  size="m"
                  target="_blank"
                  href={`/product/${productPageName}`}
                  variant="normal"
                  data-testid="main-product-title">
                  {displayedName}
                </LinkButton>
              ) : (
                <Typography
                  variant="m"
                  size="body_long"
                  color="text-primary"
                  data-testid="main-product-title">
                  {displayedName}
                </Typography>
              )}
            </MainProductName>
            <PriceColumnContainer data-testid="main-product-price">
              <Price value={price} variant="medium" align="right" size="l" units="₽" fraction />
              {Number(count) > 1 ? (
                <Price
                  align="right"
                  size="xs"
                  units={priceUnitsLabel}
                  value={Number(pricePerOneItem)}
                  variant="medium"
                  fraction
                />
              ) : null}
            </PriceColumnContainer>
          </MainProductInfoRow>

          <CounterAndSelectTintColorContainer>
            {selectTintColorComponent}
            <div>
              <Counter size="s" min={1} value={count} onChange={onChangeCounter} />
            </div>
          </CounterAndSelectTintColorContainer>
        </MainProductInfoColumn>
      </MainProductImageAndName>
    </MainProductContainer>
  );
};
